import Button from "@/componentsDS/Button/Button";
import { ButtonType, ButtonVariant } from "@/componentsDS/Button/Button.type";
import Icon from "@/componentsDS/Icon/Icon";
import { IconSize, IconStyle } from "@/componentsDS/Icon/Icon.type";
import Table from "@/componentsDS/Table/Table";
import React, { useEffect, useMemo, useState } from "react";
import useModal from "@/hooks/use-modal/index";
import { EditModelTermModal } from "../modals/edit-model-term-modal";
import { DeleteModelTerm } from "../modals/delete-model-term-modal";
import useTermClause from "@/hooks/use-term-clause/index";
import { useNavigate } from "@/lib/router-dom/index";
import Toast from "@/components/Toast/index";
import PropTypes from "prop-types";
import moment from "moment";
import { useSelector } from "react-redux";
import { featureConstant } from '@/constants/configuration/feature';
import { useFeature } from "@/hooks/use-feature/index.js";
import { renderUserEmailStacked, getTooltipAttributes } from "@/utils/formatUserEmail";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@dextech-ai/dex-design-system";

const ModelTermsTable = ({ setCreateModelTerm, termTemplate }) => {
	const isSoftDeleteUserEnabled = useFeature(featureConstant.SOFT_DELETE_USER);
	const [selectedRow, setSelectedRow] = useState();
	const {
		show: showEditModelTerm,
		hide: hideEditModelTerm,
		visible: isEditModelTermVisible,
	} = useModal();
	const {
		show: showDeleteModelTerm,
		hide: hideDeleteModelTerm,
		visible: isDeleteModelTermVisible,
	} = useModal();

	const {
		termClauseList,
		termClauseListMeta,
		paginationData,
		termClauseLoading,
		toastList,
		sort,
		fetchTermClauseList,
		updateTermClauseName,
		deleteTermClause,
		onNext,
		onPrevious,
		onPageChange,
		onPageSizeChange,
		setSort,
	} = useTermClause({
		page: 1,
		pageSize: 10,
		sortConfig: { key: "modified", direction: "desc" },
	});

	const { goTo } = useNavigate();
	const profile = useSelector((state) => state.auth.profile);
	const orgId = profile?.organizationId;

	useEffect(() => {
		fetchTermClauseList({ orgId });
	}, []);

	const editModelTermHandler = async (clauseName) => {
		const data = await updateTermClauseName(selectedRow?.id, {
			clauseName,
		});
		if (data) {
			fetchTermClauseList({ orgId });
			hideEditModelTerm();
		}
	};

	const deleteModelTermHandler = async () => {
		hideDeleteModelTerm();
		const response = await deleteTermClause(selectedRow?.id);
		if (response) {
			fetchTermClauseList({ orgId });
		}
	};

	const onSortHandler = async (sortConfig) => {
		setSort(sortConfig);
		fetchTermClauseList({ ...sortConfig, orgId });
	};

	const modelHeaders = useMemo(() => {
		return [
			{
				key: "clauseName",
				label: "Template Name",
				width: "400px",
				sortable: true,
				render: (key, row) => {
					return (
						<div
							onClick={() =>
								goTo(`/setting/edit-clause/${row.id}`)
							}
							role="none"
							className="account-settings__modal_term-list__term-name"
						>
							{row.clauseName}
						</div>
					);
				},
			},
			{
				key: "created",
				label: "Created On",
				width: "300px",
				sortable: true,
				render: (_, row) => {
					return (
						<div onClick={() => goTo(`/setting/edit-clause/${row.id}`)} className="account-settings__modal_term-list__term-name" role="none">
							<div>{moment(row.created).format('DD/MM/YYYY h:mm A')}</div>
							<div className="account-settings__modal_term-list__term-date" {...getTooltipAttributes(isSoftDeleteUserEnabled, row.createdBy)}>by {isSoftDeleteUserEnabled ? renderUserEmailStacked(row.createdBy) : row.createdBy}</div>
						</div>
					);
				},
			},
			{
				key: "modified",
				label: "Last Modified",
				width: "300px",
				sortable: true,
				render: (_, row) => {
					return (
						<div onClick={() => goTo(`/setting/edit-clause/${row.id}`)} className="account-settings__modal_term-list__term-name" role="none">
							<div>{moment(row.modified).format("DD/MM/YYYY h:mm A")}</div>
							<div className="account-settings__modal_term-list__term-date" {...getTooltipAttributes(isSoftDeleteUserEnabled, row.modifiedBy)}>by {isSoftDeleteUserEnabled ? renderUserEmailStacked(row.modifiedBy) : row.modifiedBy}</div>
						</div>
					);
				},
			},
			{
				key: "action",
				width: "150px",
				actionable: true,
				render: (_, row) => {
					return (
						<DropdownMenu placement="bottomRight">
							<DropdownMenuTrigger asChild>
								<Button
									variant={ButtonVariant.UTILITY}
									type={ButtonType.UTILITY}
									dataTestId="term-row-actions"
									className="account-settings__modal_term-list__actions"
									text={
										<Icon
											name="ellipsis-vertical"
											size={IconSize.MEDIUM}
											style={IconStyle.SOLID}
										/>
									}
								/>
							</DropdownMenuTrigger>
							<DropdownMenuContent>
								<DropdownMenuItem
									itemKey="rename"
									onSelect={() => {
										setSelectedRow(row);
										showEditModelTerm();
									}}
								>
									Rename
								</DropdownMenuItem>
								<DropdownMenuItem
									itemKey="delete"
									onSelect={() => {
										setSelectedRow(row);
										showDeleteModelTerm();
									}}
								>
									Delete
								</DropdownMenuItem>
							</DropdownMenuContent>
						</DropdownMenu>
					);
				},
			},
		];
	}, []);

	const customGroupHeaderRender = () => {
		return (
			<Button
				className="account-settings__modal_term-action"
				text="Create New Template"
				type={ButtonType.SOLID}
				variant={ButtonVariant.STANDARD}
				onClick={() => setCreateModelTerm(true)}
				disabled={termClauseLoading || !termTemplate}
			/>
		);
	};

	const tableHelperText = () => {
		return (
			<div className="account-settings__modal_term-info">
				<Icon
					name="information-circle"
					size={IconSize.MEDIUM}
					style={IconStyle.SOLID}
				/>
				<div className="account-settings__modal_term-info-text">
					These are user templates created by the Organisation Admin
					for their organisation’s ETR users to issue MLETR-compliant
					electronic bills of lading (“eBLs”). Here, Organisation
					Admin can create, review and update certain variables in the
					standard Additional Terms and Conditions. When ETR users
					issue an eBL, they may simply select from these
					pre-populated templates for convenience whilst ensuring that
					the eBL are compliant with MLETR requirements.
				</div>
			</div>
		);
	};

	return (
		<>
			<Table
				headers={modelHeaders}
				additionalClasses={"account-settings__modal_term-container"}
				data={termClauseList ?? []}
				loading={termClauseLoading}
				title="User Created eBL Templates"
				isCollapsible={true}
				tableHelperText={tableHelperText}
				customGroupHeaderRender={customGroupHeaderRender}
				emptyMessage="No items available."
				pagination={{
					meta: {
						...termClauseListMeta,
						currentPage: paginationData.page,
						pageSize: paginationData.pageSize,
					},
					onNext: () => onNext(orgId),
					onPrevious: () => onPrevious(orgId),
					onPageChange: (page) => onPageChange(page, orgId),
					onPageSizeChange: (pageSize) => onPageSizeChange(pageSize, orgId),
				}}
				onSort={onSortHandler}
				sortConfig={sort}
			/>
			<EditModelTermModal
				isModalVisible={isEditModelTermVisible}
				onEditHandler={editModelTermHandler}
				hideModal={hideEditModelTerm}
				name={selectedRow?.clauseName}
			/>
			<DeleteModelTerm
				isModalVisible={isDeleteModelTermVisible}
				onDeleteHandler={deleteModelTermHandler}
				hideModal={hideDeleteModelTerm}
				termClauseCount={termClauseListMeta?.count ?? 0}
			/>
			<Toast toastList={toastList} />
		</>
	);
};

export default ModelTermsTable;

ModelTermsTable.propTypes = {
	setCreateModelTerm: PropTypes.func,
	termTemplate: PropTypes.object,
};
